import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Download, Inbox, RefreshCw } from "lucide-react";
import { Logo } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  neu: "Neu",
  kontaktiert: "Kontaktiert",
  besichtigung: "Besichtigung vereinbart",
  angebot_gesendet: "Angebot gesendet",
  gewonnen: "Gewonnen",
  verloren: "Verloren",
};
const STATUS_CLS = {
  neu: "bg-forest/10 text-forest border-forest/20",
  kontaktiert: "bg-gold/20 text-gold-dark border-gold/40",
  besichtigung: "bg-gold/20 text-gold-dark border-gold/40",
  angebot_gesendet: "bg-forest/15 text-forest border-forest/30",
  gewonnen: "bg-green-100 text-green-800 border-green-300",
  verloren: "bg-charcoal/10 text-charcoal-muted border-charcoal/20",
};

export default function Admin() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();
  const cfg = { withCredentials: true };

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`, {}, cfg); } catch { /* ignore */ }
    navigate("/admin/login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/submissions`, cfg);
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Anfragen konnten nicht geladen werden.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
    try {
      await axios.patch(`${API}/admin/submissions/${id}`, { status }, cfg);
    } catch {
      toast.error("Status konnte nicht aktualisiert werden.");
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await axios.get(`${API}/files/${fileId}`, { ...cfg, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Datei konnte nicht geladen werden.");
    }
  };

  return (
    <div className="min-h-screen bg-sand">
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo /><span className="text-sm text-charcoal-muted hidden sm:inline">Anfragen-Verwaltung</span></div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-charcoal hover:border-forest hover:text-forest transition-colors"><RefreshCw className="w-4 h-4" /> Aktualisieren</button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-md bg-forest px-3 py-2 text-sm text-white hover:bg-forest-dark transition-colors"><LogOut className="w-4 h-4" /> Abmelden</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading font-extrabold text-2xl text-charcoal mb-1">Eingegangene Anfragen</h1>
        <p className="text-charcoal-muted mb-6">{items ? `${items.length} Anfrage(n)` : "Wird geladen …"}</p>

        {items === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-charcoal-muted"><Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />Noch keine Anfragen eingegangen.</div>
        ) : (
          <div className="space-y-4">
            {items.map((it) => (
              <article key={it.id} data-testid="admin-submission" className="bg-white rounded-xl border border-border p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-heading font-bold text-lg text-charcoal">{it.name} {it.company ? <span className="text-charcoal-muted font-normal">· {it.company}</span> : null}</h2>
                    <p className="text-sm text-charcoal-muted">{new Date(it.created_at).toLocaleString("de-DE")} · {it.customer_type}</p>
                  </div>
                  <span className={`text-xs font-medium rounded-full px-3 py-1 border ${STATUS_CLS[it.status] || STATUS_CLS.neu}`}>{STATUS[it.status] || "Neu"}</span>
                </div>

                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                  <p><span className="text-charcoal-muted">Leistung:</span> <span className="font-medium text-charcoal">{it.service}</span></p>
                  <p><span className="text-charcoal-muted">Telefon:</span> <a href={`tel:${it.phone}`} className="font-medium text-forest">{it.phone}</a></p>
                  <p><span className="text-charcoal-muted">E-Mail:</span> <a href={`mailto:${it.email}`} className="font-medium text-forest break-all">{it.email}</a></p>
                  <p><span className="text-charcoal-muted">PLZ / Ort:</span> <span className="font-medium text-charcoal">{it.postal_code} {it.city}</span></p>
                  {it.object_type && <p><span className="text-charcoal-muted">Objekt:</span> <span className="font-medium text-charcoal">{it.object_type}</span></p>}
                  {it.project_size && <p><span className="text-charcoal-muted">Größe:</span> <span className="font-medium text-charcoal">{it.project_size}</span></p>}
                  {it.preferred_start && <p><span className="text-charcoal-muted">Beginn:</span> <span className="font-medium text-charcoal">{it.preferred_start}</span></p>}
                </div>

                <p className="mt-3 text-charcoal bg-sand border border-border rounded-lg p-3 text-sm whitespace-pre-wrap">{it.message}</p>

                {it.files?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {it.files.map((f) => (
                      <button key={f.id} onClick={() => downloadFile(f.id, f.filename)} data-testid="admin-download" className="inline-flex items-center gap-2 text-sm rounded-md border border-border px-3 py-1.5 text-forest hover:border-forest transition-colors"><Download className="w-4 h-4" /> {f.filename}</button>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {Object.entries(STATUS).map(([k, v]) => (
                    <button key={k} onClick={() => setStatus(it.id, k)} className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${it.status === k ? "bg-forest text-white border-forest" : "bg-white text-charcoal-muted border-border hover:border-forest"}`}>{v}</button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
